import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "../sentry";
import { ErrorFallback } from "./ErrorFallback";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

/**
 * Catches render-time errors thrown by descendants of the dashboard's main
 * content area. Sidebar + Header sit outside this boundary and stay
 * functional, so the user can navigate away from a broken page without
 * having to refresh the whole shell.
 *
 * Forwards the error to Sentry (ADR-087) and renders ErrorFallback, which
 * composes a one-click bug report through the existing sendSupportRequest
 * pipeline (ADR-071, ADR-104).
 *
 * The boundary is keyed by route in DashboardLayout so navigating to a new
 * page implicitly resets it. A "Try again" button on the fallback also
 * resets state for in-place recovery.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack ?? "" },
      },
    });
  }

  reset = (): void => {
    this.setState({ error: null, componentStack: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.componentStack}
          onReset={this.reset}
        />
      );
    }
    return this.props.children;
  }
}
